import { z } from 'zod';

// SupplierIdSchema is exported for use with branded ID parameters.
// The id field in SupplierSchema itself is unbranded z.number() to match
// the drizzle-zod selectSuppliersSchema output (bigint mode:'number' → z.number()).
export const SupplierIdSchema = z.number().brand<'SupplierId'>();
export type SupplierId = z.infer<typeof SupplierIdSchema>;

const SupplierBaseSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  code: z.string().nullable(),
  shippingAddress: z.string().nullish(),
  isInternal: z.boolean(),
  deletedAt: z.date().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

const SupplierDateFields = {
  deletedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
};

export const SupplierSchema = SupplierBaseSchema.extend(SupplierDateFields);
export type Supplier = z.infer<typeof SupplierSchema>;

export const SupplierListItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  code: z.string().nullable(),
  shippingAddress: z.string().nullish(),
  isInternal: z.boolean(),
  mediaUnitCount: z.number().int().nonnegative(),
});
export type SupplierListItem = z.infer<typeof SupplierListItemSchema>;

export const CreateSupplierSchema = SupplierSchema.omit({
  id: true,
  isInternal: true,
  deletedAt: true,
  createdAt: true,
  updatedAt: true,
})
  .extend({
    email: SupplierSchema.shape.email.optional(),
    phone: SupplierSchema.shape.phone.optional(),
    code: SupplierSchema.shape.code.optional(),
    shippingAddress: SupplierSchema.shape.shippingAddress.optional(),
  })
  .strict();
export type CreateSupplier = z.infer<typeof CreateSupplierSchema>;

export const UpdateSupplierSchema = CreateSupplierSchema.partial().strict();
export type UpdateSupplier = z.infer<typeof UpdateSupplierSchema>;

export const ListSuppliersQuerySchema = z
  .object({
    q: z.string().trim().min(1).optional(),
  })
  .strict();
export type ListSuppliersQuery = z.infer<typeof ListSuppliersQuerySchema>;

export const DeleteSupplierBodySchema = z
  .discriminatedUnion('strategy', [
    z.object({ strategy: z.literal('delete_units') }),
    z.object({
      strategy: z.literal('reassign'),
      targetSupplierId: z.number().int().positive(),
    }),
  ])
  .optional();
export type DeleteSupplierBody = z.infer<typeof DeleteSupplierBodySchema>;

/**
 * Flat base object for DELETE /suppliers/:id — no refinements so that consumers
 * can call .omit() (Zod v4 forbids .omit() on refined schemas).
 * The `id` path param is consumed by callEndpoint; remaining fields become the body.
 * Uses unbranded z.number() for `id` so callers can pass a plain SupplierListItem.id.
 */
export const DeleteSupplierRequestBaseSchema = z
  .object({
    id: z.number(),
    strategy: z.enum(['delete_units', 'reassign']).optional(),
    targetSupplierId: z.number().int().positive().optional(),
  })
  .strict();

/**
 * Validated flat request schema for DELETE /suppliers/:id.
 * Adds superRefine: when strategy === 'reassign', targetSupplierId is required.
 * Use DeleteSupplierRequestBaseSchema when .omit() is needed (e.g. controller DTO).
 */
export const DeleteSupplierRequestSchema = DeleteSupplierRequestBaseSchema.superRefine(
  (val, ctx) => {
    if (val.strategy === 'reassign' && val.targetSupplierId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetSupplierId'],
        message: 'targetSupplierId is required when strategy is reassign',
      });
    }
  },
);
export type DeleteSupplierRequest = z.infer<typeof DeleteSupplierRequestSchema>;
